import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { WorkflowDetalhe, WorkflowEtapa, WorkflowEtapaTipo } from '../../core/models/workflow.models';
import { WorkflowsService } from '../../core/services/workflows.service';

type StatusFilter = 'TODOS' | 'EM_ANDAMENTO' | 'CONCLUIDO';

interface EtapaRecord {
  etapaNome: string;
  etapaTipo: WorkflowEtapaTipo;
  osNumero: string;
  placa: string;
  veiculo: string;
  seguradora: string;
  responsavel: string;
  realInicio: Date | null;
  realFim: Date | null;
  previstoInicio: Date | null;
  previstoFim: Date | null;
  diasNaEtapa: number;
  isConcluida: boolean;
  atrasada: boolean;
  atrasoDias: number;
  previstoDias: number | null;
}

interface EtapaResumo {
  etapaNome: string;
  etapaTipo: WorkflowEtapaTipo;
  amostras: number;
  media: number;
  mediana: number;
  p95: number;
  min: number;
  max: number;
  emAndamento: number;
  atrasadas: number;
  sla: number | null;
}

@Component({
  selector: 'app-relatorios-processo-tempo-etapa',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './relatorios-processo-tempo-etapa.component.html',
  styleUrl: './relatorios-processo-tempo-etapa.component.scss',
})
export class RelatoriosProcessoTempoEtapaComponent implements OnInit, OnDestroy {
  workflows: WorkflowDetalhe[] = [];
  records: EtapaRecord[] = [];
  filtered: EtapaRecord[] = [];
  resumoPorEtapa: EtapaResumo[] = [];
  criticalRecords: EtapaRecord[] = [];

  filterStart = '';
  filterEnd = '';
  filterSeguradora = 'TODOS';
  filterResponsavel = 'TODOS';
  filterEtapa: WorkflowEtapaTipo | 'TODAS' = 'TODAS';
  filterStatus: StatusFilter = 'TODOS';
  onlyConcluidas = true;
  outlierLimit = 20;

  seguradoras: string[] = [];
  responsaveis: string[] = [];
  etapas: Array<{ tipo: WorkflowEtapaTipo; nome: string }> = [];

  totalEtapas = 0;
  etapaMaisLenta: EtapaResumo | null = null;
  etapaMaisInstavel: EtapaResumo | null = null;
  percentualAtraso = 0;
  tempoMedioGeral = 0;
  maxP95 = 0;

  activeTab: 'resumo' | 'distribuicao' | 'criticos' = 'resumo';

  private subscription = new Subscription();

  constructor(private workflowsService: WorkflowsService) {}

  ngOnInit(): void {
    const today = new Date();
    const endStr = this.toDateInput(today);
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    this.filterStart = this.toDateInput(start);
    this.filterEnd = endStr;

    this.subscription.add(
      this.workflowsService.getAllDetails().subscribe((workflows) => {
        this.workflows = workflows;
        this.etapas = this.buildEtapas(workflows);
        this.seguradoras = this.buildList((wf) => wf.seguradoraNome);
        this.responsaveis = this.buildList((wf) => wf.responsavelAtual);
        this.records = this.flattenEtapas(workflows);
        this.applyFilters();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  applyFilters(): void {
    const start = this.filterStart ? new Date(`${this.filterStart}T00:00:00`) : null;
    const end = this.filterEnd ? new Date(`${this.filterEnd}T23:59:59`) : null;
    const now = new Date();

    this.filtered = this.records.filter((record) => {
      const workflow = this.workflows.find((wf) => this.getOsNumero(wf) === record.osNumero);
      const dataInicio = workflow ? this.getWorkflowInicio(workflow) : record.realInicio;

      if (start && dataInicio && dataInicio < start) return false;
      if (end && dataInicio && dataInicio > end) return false;
      if (this.filterSeguradora !== 'TODOS' && record.seguradora !== this.filterSeguradora) return false;
      if (this.filterResponsavel !== 'TODOS' && record.responsavel !== this.filterResponsavel) return false;
      if (this.filterEtapa !== 'TODAS' && record.etapaTipo !== this.filterEtapa) return false;
      if (this.filterStatus !== 'TODOS') {
        const isFinal = workflow ? this.isFinalizado(workflow) : false;
        if (this.filterStatus === 'CONCLUIDO' && !isFinal) return false;
        if (this.filterStatus === 'EM_ANDAMENTO' && isFinal) return false;
      }
      if (this.onlyConcluidas && !record.isConcluida) return false;
      if (record.realInicio === null) return false;

      return true;
    });

    this.rebuildMetrics(now);
  }

  private rebuildMetrics(now: Date): void {
    const byEtapa = this.groupByEtapa(this.filtered);
    this.resumoPorEtapa = Object.values(byEtapa).map((group) => this.calcResumo(group));
    this.resumoPorEtapa.sort((a, b) => b.media - a.media);

    this.totalEtapas = this.filtered.length;
    this.etapaMaisLenta = this.resumoPorEtapa[0] ?? null;
    this.etapaMaisInstavel = this.resumoPorEtapa.reduce<EtapaResumo | null>((acc, current) => {
      if (!acc) return current;
      const rangeCurrent = current.max - current.min;
      const rangeAcc = acc.max - acc.min;
      return rangeCurrent > rangeAcc ? current : acc;
    }, null);
    const atrasadas = this.filtered.filter((record) => record.atrasada).length;
    this.percentualAtraso = this.totalEtapas ? (atrasadas / this.totalEtapas) * 100 : 0;
    const medias = this.filtered.map((record) => record.diasNaEtapa);
    this.tempoMedioGeral = medias.length ? this.calcMedia(medias) : 0;
    this.maxP95 = Math.max(0, ...this.resumoPorEtapa.map((item) => item.p95));

    this.criticalRecords = [...this.filtered]
      .sort((a, b) => b.diasNaEtapa - a.diasNaEtapa)
      .filter((record) => record.diasNaEtapa >= this.outlierLimit);
  }

  private flattenEtapas(workflows: WorkflowDetalhe[]): EtapaRecord[] {
    const now = new Date();
    const records: EtapaRecord[] = [];

    workflows.forEach((wf) => {
      const osNumero = this.getOsNumero(wf);
      const placa = wf.placa || '—';
      const veiculo = [wf.marca, wf.modelo].filter(Boolean).join(' ');
      const seguradora = wf.seguradoraNome || '—';
      const responsavel = wf.responsavelAtual || '—';

      wf.etapas.forEach((etapa) => {
        const realInicio = this.toDate(etapa.inicio);
        const realFim = this.toDate(etapa.fim);
        const previstoInicio = this.toDate(etapa.previstaInicio);
        const previstoFim = this.toDate(etapa.previstaFim);

        if (!realInicio) {
          return;
        }

        const isConcluida = Boolean(realFim);
        const fimBase = realFim ?? now;
        const diasNaEtapa = this.diffDias(realInicio, fimBase);
        const atrasoDias = previstoFim ? this.diffDias(previstoFim, fimBase) : 0;
        const atrasada = Boolean(previstoFim && fimBase > previstoFim);
        const previstoDias =
          previstoInicio && previstoFim ? this.diffDias(previstoInicio, previstoFim) : null;

        records.push({
          etapaNome: etapa.nome,
          etapaTipo: etapa.tipo,
          osNumero,
          placa,
          veiculo,
          seguradora,
          responsavel,
          realInicio,
          realFim,
          previstoInicio,
          previstoFim,
          diasNaEtapa,
          isConcluida,
          atrasada,
          atrasoDias: Math.max(0, atrasoDias),
          previstoDias,
        });
      });
    });

    return records;
  }

  private groupByEtapa(records: EtapaRecord[]): Record<string, EtapaRecord[]> {
    return records.reduce<Record<string, EtapaRecord[]>>((acc, record) => {
      const key = record.etapaNome;
      if (!acc[key]) acc[key] = [];
      acc[key].push(record);
      return acc;
    }, {});
  }

  private calcResumo(records: EtapaRecord[]): EtapaResumo {
    const valores = records.map((record) => record.diasNaEtapa).sort((a, b) => a - b);
    const media = this.calcMedia(valores);
    const mediana = this.calcMediana(valores);
    const p95 = this.calcP95(valores);
    const min = valores.length ? valores[0] : 0;
    const max = valores.length ? valores[valores.length - 1] : 0;
    const emAndamento = records.filter((record) => !record.isConcluida).length;
    const atrasadas = records.filter((record) => record.atrasada).length;
    const slaTotal = records.filter((record) => record.previstoFim).length;
    const slaDentro = records.filter(
      (record) => record.previstoFim && !record.atrasada
    ).length;
    const sla = slaTotal ? Math.round((slaDentro / slaTotal) * 100) : null;

    return {
      etapaNome: records[0]?.etapaNome ?? '—',
      etapaTipo: records[0]?.etapaTipo ?? WorkflowEtapaTipo.ABERTURA_OS,
      amostras: records.length,
      media,
      mediana,
      p95,
      min,
      max,
      emAndamento,
      atrasadas,
      sla,
    };
  }

  isGargalo(item: EtapaResumo): boolean {
    return item.media > 7 || item.p95 > 7;
  }

  getDistribuicaoWidth(valor: number): string {
    if (!this.maxP95) return '0%';
    return `${Math.min(100, Math.round((valor / this.maxP95) * 100))}%`;
  }

  getEtapaLabel(tipo: WorkflowEtapaTipo): string {
    return this.etapas.find((etapa) => etapa.tipo === tipo)?.nome ?? String(tipo);
  }

  formatOsNumero(record: EtapaRecord): string {
    return record.osNumero || '—';
  }

  formatDate(date: Date | null): string {
    return date ? date.toLocaleDateString('pt-BR') : '—';
  }

  private buildList(getter: (wf: WorkflowDetalhe) => string | undefined): string[] {
    const set = new Set<string>();
    this.workflows.forEach((wf) => {
      const value = getter(wf);
      if (value && value.trim()) {
        set.add(value.trim());
      }
    });
    return Array.from(set).sort();
  }

  private buildEtapas(workflows: WorkflowDetalhe[]): Array<{ tipo: WorkflowEtapaTipo; nome: string }> {
    const map = new Map<WorkflowEtapaTipo, string>();
    workflows.forEach((wf) => {
      wf.etapas.forEach((etapa) => {
        if (!map.has(etapa.tipo)) {
          map.set(etapa.tipo, etapa.nome);
        }
      });
    });
    return Array.from(map.entries()).map(([tipo, nome]) => ({ tipo, nome }));
  }

  private getWorkflowInicio(wf: WorkflowDetalhe): Date {
    return wf.criadoEm instanceof Date ? wf.criadoEm : new Date(wf.criadoEm);
  }

  private isFinalizado(wf: WorkflowDetalhe): boolean {
    return wf.etapaAtual === WorkflowEtapaTipo.FINALIZADO || Boolean(wf.etapas.find((etapa) => etapa.tipo === WorkflowEtapaTipo.FINALIZADO && etapa.fim));
  }

  private getOsNumero(wf: WorkflowDetalhe): string {
    return wf.osNumero || `OS-${String(wf.id).padStart(6, '0')}`;
  }

  private toDate(value: Date | string | null | undefined): Date | null {
    if (!value) return null;
    return value instanceof Date ? value : new Date(value);
  }

  private diffDias(start: Date, end: Date): number {
    return Math.max(0, Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
  }

  private calcMedia(values: number[]): number {
    if (!values.length) return 0;
    return Math.round(values.reduce((acc, value) => acc + value, 0) / values.length);
  }

  private calcMediana(values: number[]): number {
    if (!values.length) return 0;
    const mid = Math.floor(values.length / 2);
    if (values.length % 2 === 0) {
      return Math.round((values[mid - 1] + values[mid]) / 2);
    }
    return values[mid];
  }

  private calcP95(values: number[]): number {
    if (!values.length) return 0;
    const index = Math.max(0, Math.ceil(0.95 * values.length) - 1);
    return values[index];
  }

  private toDateInput(date: Date): string {
    return date.toISOString().slice(0, 10);
  }
}

